import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import axios from 'axios';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { SiteSeoProvider } from './context/SiteSeoContext';
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { SubscribeModalProvider } from './context/SubscribeModalContext';
import ErrorBoundary from './components/ui/ErrorBoundary';
import SkipToContent from './components/ui/SkipToContent';
import { store, persistor } from './store/configureStore';
import { hidePrerenderStaticContent } from './utils/prerenderMeta';
import './i18n/config'; // Initialize i18n
import Index from './pages/Index';
import ArticleDetail from './pages/ArticleDetail';
import Category from './pages/Category';
import Categories from './pages/Categories';
import Author from './pages/Author';
import Authors from './pages/Authors';
import Trending from './pages/Trending';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Search from './pages/Search';
import Bookmarks from './pages/Bookmarks';
import ConfirmSubscription from './pages/ConfirmSubscription';
import Unsubscribe from './pages/Unsubscribe';
import NotFound from './pages/NotFound';
import Error500 from './pages/Error500';
import GoogleAnalytics from './components/seo/GoogleAnalytics';
import VisitorTracking from './components/seo/VisitorTracking';
import PopupAdController from './components/ads/PopupAdController';
import AuthBootstrap from './components/auth/AuthBootstrap';
import Login from './pages/Login';
import Register from './pages/Register';
import Account from './pages/Account';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const TENANT_SLUG = (process.env.REACT_APP_TENANT_SLUG || '').trim().toLowerCase();
if (TENANT_SLUG) {
  axios.defaults.headers.common['x-tenant-slug'] = TENANT_SLUG;
}

/**
 * Prerender injects a visible <article id="prerender-static-content"> above #root
 * for crawlers (SEO). Hide it once React is running on non-article pages.
 * ArticleDetail keeps it until the real article loads (soft-404 / loading UX).
 */
function PrerenderStaticCleanup() {
  const { pathname } = useLocation();

  useEffect(() => {
    const isArticle = /(?:^|\/)article\//.test(pathname || '/');
    if (!isArticle) {
      hidePrerenderStaticContent();
    }
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <SiteSeoProvider>
              <ThemeProvider>
                <BrowserRouter>
                  <AuthBootstrap />
                  <GoogleAnalytics />
                  <VisitorTracking />
                  <LanguageProvider>
                    <SubscribeModalProvider>
                    <SkipToContent />
                    <PrerenderStaticCleanup />
                    <Routes>
                      {/* Default routes (no region prefix - defaults to US/English) */}
                      <Route path="/" element={<Index />} />
                      <Route path="/article/:slug" element={<ArticleDetail />} />
                      <Route path="/category/:slug" element={<Category />} />
                      <Route path="/categories" element={<Categories />} />
                      <Route path="/author/:slug" element={<Author />} />
                      <Route path="/authors" element={<Authors />} />
                      <Route path="/trending" element={<Trending />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/search" element={<Search />} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/account" element={<Account />} />
                      <Route path="/bookmarks" element={<Bookmarks />} />
                      <Route path="/confirm-subscription" element={<ConfirmSubscription />} />
                      <Route path="/unsubscribe" element={<Unsubscribe />} />
                      <Route path="/500" element={<Error500 />} />
                      
                      {/* Region-specific routes (accept any 2-letter region code, validation in LanguageContext) */}
                      <Route path="/:region" element={<Index />} />
                      <Route path="/:region/article/:slug" element={<ArticleDetail />} />
                      <Route path="/:region/category/:slug" element={<Category />} />
                      <Route path="/:region/categories" element={<Categories />} />
                      <Route path="/:region/author/:slug" element={<Author />} />
                      <Route path="/:region/authors" element={<Authors />} />
                      <Route path="/:region/trending" element={<Trending />} />
                      <Route path="/:region/about" element={<About />} />
                      <Route path="/:region/contact" element={<Contact />} />
                      <Route path="/:region/privacy" element={<Privacy />} />
                      <Route path="/:region/terms" element={<Terms />} />
                      <Route path="/:region/search" element={<Search />} />
                      <Route path="/:region/login" element={<Login />} />
                      <Route path="/:region/register" element={<Register />} />
                      <Route path="/:region/verify-email" element={<VerifyEmail />} />
                      <Route path="/:region/forgot-password" element={<ForgotPassword />} />
                      <Route path="/:region/reset-password" element={<ResetPassword />} />
                      <Route path="/:region/account" element={<Account />} />
                      <Route path="/:region/bookmarks" element={<Bookmarks />} />
                      <Route path="/:region/confirm-subscription" element={<ConfirmSubscription />} />
                      <Route path="/:region/unsubscribe" element={<Unsubscribe />} />
                      <Route path="/:region/500" element={<Error500 />} />
                      
                      {/* 404 route */}
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                    <PopupAdController />
                    </SubscribeModalProvider>
                  </LanguageProvider>
                </BrowserRouter>
              </ThemeProvider>
              </SiteSeoProvider>
            </HelmetProvider>
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
}

export default App;
